import React, { createContext, useState, useEffect } from 'react';
import { trackShopifyAddToCart } from '../utils';

import Client from 'shopify-buy';

const client = Client.buildClient({
  domain: process.env.GATSBY_SHOPIFY_STORE_URL,
  storefrontAccessToken: process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
});

const defaultValues = {
  isOverlayActive: false,
  setOverlayActive: () => {},

  isCartOpen: false,
  toggleCartOpen: () => {},

  isSearchOpen: false,
  setSearchOpen: () => {},

  client,
  checkout: {
    id: 0,
    lineItems: [],
    subtotalPrice: 0,
    webUrl: '',
  },
  addProductsToCheckout: () => {},
  updateProductInCheckout: () => {},
  removeProductFromCheckout: () => {},
  updateCheckoutAttributes: () => {},
};

export const StoreContext = createContext(defaultValues);

const getCustomAttribute = (attributes = [], key) =>
  attributes.find(attribute => attribute.key === key)?.value;

const getM2Configuration = (attributes = []) =>
  JSON.stringify(
    attributes
      .filter(attribute => attribute.key !== 'Qty')
      .map(({ key, value }) => ({
        key: String(key),
        value: String(value),
      }))
      .sort((a, b) => a.key.localeCompare(b.key)),
  );

export const StoreProvider = ({ children }) => {
  const [isOverlayActive, setOverlayActive] = useState(
    defaultValues.isOverlayActive,
  );
  const [isCartOpen, setCartOpen] = useState(defaultValues.isCartOpen);
  const [isSearchOpen, setSearchOpen] = useState(defaultValues.isSearchOpen);
  const [checkout, setCheckout] = useState(defaultValues.checkout);

  const isBrowser = typeof window !== 'undefined';
  const localStorageCheckoutIdKey = `${process.env.GATSBY_SHOPIFY_SHOP_NAME}:checkout-id`;

  useEffect(() => {
    initialiseCheckout();
  }, []);

  const toggleCartOpen = () => {
    setCartOpen(!isCartOpen);
  };

  const initialiseCheckout = async () => {
    const checkoutId = isBrowser
      ? localStorage.getItem(localStorageCheckoutIdKey)
      : null;

    let newCheckout;

    if (checkoutId) {
      try {
        newCheckout = await client.checkout.fetch(checkoutId);

        if (
          newCheckout === null ||
          newCheckout.completedAt ||
          newCheckout.lineItems.find(({ variant }) => variant === null)
        ) {
          newCheckout = await getNewCheckout();
        }
      } catch (e) {
        console.error(e);
        newCheckout = await getNewCheckout();
      }
    } else {
      newCheckout = await getNewCheckout();
    }

    setCheckout(newCheckout);
  };

  const getNewCheckout = async () => {
    try {
      const newCheckout = await client.checkout.create();

      if (isBrowser) {
        localStorage.setItem(localStorageCheckoutIdKey, newCheckout.id);
      }

      return newCheckout;
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  const addProductsToCheckout = async (lineItems, analytics) => {
    try {
      const [lineItem] = lineItems;
      const attributes = lineItem.customAttributes || [];

      const width = Number(getCustomAttribute(attributes, 'Width'));
      const height = Number(getCustomAttribute(attributes, 'Height'));
      const requestedItemQuantity = Number(
        getCustomAttribute(attributes, 'Qty'),
      );

      const isM2Product = width > 0 && height > 0 && requestedItemQuantity > 0;

      const matchingM2Line = isM2Product
        ? checkout.lineItems.find(
            checkoutLine =>
              checkoutLine.variant?.id === lineItem.variantId &&
              getM2Configuration(checkoutLine.customAttributes) ===
                getM2Configuration(attributes),
          )
        : null;

      let updatedCheckout;

      if (matchingM2Line) {
        const existingItemQuantity = Number(
          getCustomAttribute(matchingM2Line.customAttributes, 'Qty'),
        );

        const totalItemQuantity = existingItemQuantity + requestedItemQuantity;

        const billingQuantity = Math.ceil(
          ((width * height) / 100) * totalItemQuantity,
        );

        const updatedAttributes = attributes.map(attribute =>
          attribute.key === 'Qty'
            ? {
                ...attribute,
                value: String(totalItemQuantity),
              }
            : attribute,
        );

        updatedCheckout = await client.checkout.updateLineItems(checkout.id, [
          {
            id: matchingM2Line.id,
            quantity: billingQuantity,
            customAttributes: updatedAttributes,
          },
        ]);
      } else {
        updatedCheckout = await client.checkout.addLineItems(
          checkout.id,
          lineItems,
        );
      }

      setCheckout(updatedCheckout);

      if (analytics) {
        await trackShopifyAddToCart({
          cartId: updatedCheckout.id,
          products: analytics.products,
          totalValue: analytics.totalValue,
        });
      }

      toggleCartOpen();

      return updatedCheckout;
    } catch (e) {
      console.error(e);
    }
  };

  const updateProductInCheckout = async (lineItemId, quantity) => {
    try {
      const updatedCheckout = await client.checkout.updateLineItems(
        checkout.id,
        [
          {
            id: lineItemId,
            quantity: quantity,
          },
        ],
      );

      setCheckout(updatedCheckout);
    } catch (e) {
      console.error(e);
    }
  };

  const removeProductFromCheckout = async lineItemId => {
    try {
      const updatedCheckout = await client.checkout.removeLineItems(
        checkout.id,
        [lineItemId],
      );

      setCheckout(updatedCheckout);
    } catch (e) {
      console.error(e);
    }
  };

  const updateCheckoutAttributes = async customAttributes => {
    try {
      const updatedCheckout = await client.checkout.updateAttributes(
        checkout.id,
        {
          customAttributes: customAttributes,
        },
      );

      setCheckout(updatedCheckout);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <StoreContext.Provider
      value={{
        ...defaultValues,

        isOverlayActive,
        setOverlayActive,

        isCartOpen,
        toggleCartOpen,

        isSearchOpen,
        setSearchOpen,

        client,
        checkout,
        addProductsToCheckout,
        updateProductInCheckout,
        removeProductFromCheckout,
        updateCheckoutAttributes,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
};
